import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IOSSwitch from "../common_components/IOSSwitch";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ROUTES } from "../routes/routes.constants";
import { DASHBOARD_ICON_RAIL_WIDTH, DASHBOARD_SIDEBAR_WIDTH, } from "./DashboardSidebar";
import { getActivePageLabel } from "./getActivePageLabel";
import { selectModules } from "../redux/userDataSlice";
import SvgIcon from "@mui/material/SvgIcon";
import { useAccountOverview } from "../context/AccountOverviewContext";
import { useDispatch } from "react-redux";
import { clearPresentationMode } from "../redux/presentationSlice";
import { selectEntityType } from "../redux/authSlice";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
import { PresentationModals } from "../common_components/PresentationModals";
export const DASHBOARD_HEADER_HEIGHT = 70;
/**
 * Fixed dashboard header from Figma:
 * - Left header block aligns with sidebar area (335px when expanded)
 * - Right block shows back arrow + page title + avatar
 */
function DashboardHeader({ isSidebarExpanded, onToggleSidebar }) {
    const location = useLocation();
    const navigate = useNavigate();
    const modules = useSelector(selectModules);
    const { activeTab, setActiveTab, setIsArchiveMode } = useAccountOverview();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useDispatch();
    const isClient = useSelector(selectEntityType) === 'client';
    const isPresentationMode = useSelector((state) => state.presentation.isPresentationMode);
    const token = useSelector((state) => state.auth.token);
    let pageTitle = getActivePageLabel(location.pathname, modules);
    if (location.pathname === ROUTES.accountOverview.path) {
        if (activeTab === 'publisher_tracker') {
            pageTitle = "Publisher tracker";
        }
        else if (activeTab === 'social_tracker') {
            pageTitle = "Social tracker";
        }
        else if (activeTab === 'youtube_tracker') {
            pageTitle = "Youtube tracker";
        }
        else if (activeTab === 'sample_tracker') {
            pageTitle = "Sample tracker";
        }
    }
    else if (location.pathname === ROUTES.addOutreachTracker.path) {
        const params = new URLSearchParams(location.search);
        const hasTrackerId = location.state?.trackerId || params.get('id') || params.get('trackerId');
        const fromPublisher = location.state?.from === 'publisher_tracker' || params.get('from') === 'publisher_tracker';
        const type = fromPublisher ? "Publisher Tracker" : "Outreach Tracker";
        pageTitle = isClient ? type : (hasTrackerId ? `Edit ${type}` : `Add ${type}`);
    }
    else if (location.pathname.startsWith('/outreach-tracker/') || location.pathname === '/affiliate-tracker/client-approval') {
        const params = new URLSearchParams(location.search);
        const fromPublisher = location.state?.from === 'publisher_tracker' || params.get('from') === 'publisher_tracker';
        pageTitle = fromPublisher ? "Publisher tracker" : "Outreach Trackers";
    }
    else if (location.pathname === ROUTES.socialYtTracker.path) {
        const params = new URLSearchParams(location.search);
        const hasTrackerId = location.state?.trackerId || params.get('id') || params.get('trackerId');
        const trackerType = location.state?.trackerType || params.get('type') || params.get('trackerType'); // 'social' or 'youtube'
        const typeString = trackerType === 'youtube' ? 'YouTube Tracker' : 'Social Tracker';
        pageTitle = isClient ? typeString : (hasTrackerId ? `Edit ${typeString}` : `Add ${typeString}`);
    }
    else if (location.pathname.startsWith('/social-yt-tracker/')) {
        const params = new URLSearchParams(location.search);
        const trackerType = location.state?.trackerType || params.get('type') || params.get('trackerType');
        const typeString = trackerType === 'youtube' ? 'YouTube Tracker' : 'Social Tracker';
        pageTitle = typeString;
    }
    else if (location.pathname.startsWith('/meeting-scorer/action/')) {
        // Meeting Scorer email-link page: it is not a menu item, so name it here
        // rather than falling back to another module's label.
        pageTitle = "Meeting Details";
    }
    const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
    const [openSelectStoreModal, setOpenSelectStoreModal] = React.useState(false);
    const handlePresentationToggle = async () => {
        if (isPresentationMode) {
            // Turn off
            try {
                const res = await ApiCall("PUT", "/api/auth/presentation-mode", { presentationMode: false, presentationClients: [], presentation_role_name: null }, { Authorization: `Bearer ${token}` });
                if (res && "data" in res && res.data?.success) {
                    dispatch(clearPresentationMode());
                    toast.success("Presentation Mode Disabled");
                    window.location.reload();
                }
                else {
                    toast.error(res?.data?.message || "Failed to disable");
                }
            }
            catch (err) {
                toast.error("Failed to disable Presentation Mode. Please try again.");
            }
        }
        else {
            // Turn on
            setOpenConfirmModal(true);
        }
    };
    const isCommissionReport = location.pathname.startsWith(ROUTES.commissionTrackerReport.path);
    const isEmployeePerformanceDetails = location.pathname.startsWith(ROUTES.employeePerformanceDetails.path);
    const isInnerPage = location.pathname.includes('/report/') ||
        location.pathname.includes('/edit') ||
        location.pathname.includes('/create') ||
        location.pathname.includes('/detail') ||
        location.pathname === ROUTES.accountOverview.path ||
        location.pathname === ROUTES.addOutreachTracker.path ||
        location.pathname.startsWith('/outreach-tracker/') ||
        location.pathname === '/affiliate-tracker/client-approval' ||
        location.pathname === ROUTES.socialYtTracker.path ||
        location.pathname.startsWith('/social-yt-tracker/') ||
        location.pathname.startsWith(ROUTES.accountPerformanceReport.path) ||
        isEmployeePerformanceDetails ||
        isCommissionReport;
    const isBackDisabled = !isInnerPage || location.pathname === ROUTES.createReport.path;
    const isClientListingPage = [
        ROUTES.activeClientList?.path,
        ROUTES.clients?.path,
        ROUTES.activeClient?.path,
        ROUTES.home?.path,
        ROUTES.moduleList?.path,
        ROUTES.userList?.path,
        ROUTES.rolesPermission?.path,
        ROUTES.root?.path,
        ROUTES.accountPerformance?.path,
        ROUTES.accountSummary?.path,
        ROUTES.commissionTracker?.path,
        ROUTES.outreachTracker?.path,
        ROUTES.syncLog?.path,
        ROUTES.affiliateAccountPerformance?.path,
        ROUTES.publisherTagging?.path,
        ROUTES.publisherReporting?.path,
        ROUTES.integrationCredentials?.path,
        ROUTES.createReport?.path,
    ].includes(location.pathname);
    return (_jsxs(AppBar, { className: "top-header-container", position: "fixed", elevation: 0, "data-testid": "top-header", sx: {
            height: DASHBOARD_HEADER_HEIGHT,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            zIndex: (t) => t.zIndex.drawer + 1,
        }, children: [_jsx(Toolbar, { disableGutters: true, sx: {
                // height: DASHBOARD_HEADER_HEIGHT,
                }, children: _jsxs(Box, { className: `header-grid ${!isSidebarExpanded && 'header-collapse'} dashboard-header-container`, sx: {
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns: `${isSidebarExpanded ? `${DASHBOARD_ICON_RAIL_WIDTH + DASHBOARD_SIDEBAR_WIDTH}px` : '60px'} 1fr`,
                        alignItems: "center",
                    }, children: [_jsx(Box, { sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: isSidebarExpanded ? `space-between` : 'center',
                                p: isSidebarExpanded ? `19px 20px` : '0',
                                width: '100%',
                                borderRight: '1px solid #F4F4F5',
                                overflow: "hidden",
                                height: '100%'
                            }, children: _jsx(Box, { sx: { p: '0' }, children: _jsx(Box, { sx: { width: isSidebarExpanded ? '124px' : '36px', height: 32, flexShrink: 0 }, children: _jsx(Box, { component: "img", src: isSidebarExpanded && !isMobile ? '/assets/logo-group8a.svg' : '/assets/collpase-logo.svg', alt: "GROUP 8A", sx: { height: '100%', width: "100%", display: "block" } }) }) }) }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 2, minWidth: 0, py: 2, px: 2.5 }, "data-testid": "page-title", children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, minWidth: 0 }, children: [(() => { return null; })(), (() => {
                                            const handleBack = () => {
                                                if (isBackDisabled)
                                                    return;
                                                setIsArchiveMode(false);
                                                if (location.pathname === ROUTES.accountOverview.path) {
                                                    navigate(ROUTES.affiliateAccountPerformance.path);
                                                    return;
                                                }
                                                if (isCommissionReport)
                                                    navigate(ROUTES.commissionTracker.path);
                                                else if (isEmployeePerformanceDetails)
                                                    navigate(ROUTES.employeePerformance.path);
                                                else if (location.pathname.startsWith('/outreach-tracker/') || location.pathname === '/affiliate-tracker/client-approval' || location.pathname === ROUTES.addOutreachTracker.path) {
                                                    navigate(ROUTES.accountOverview.path, { state: { restoreTab: 'publisher_tracker' } });
                                                }
                                                else if (location.pathname === ROUTES.socialYtTracker.path || location.pathname.startsWith('/social-yt-tracker/')) {
                                                    const params = new URLSearchParams(location.search);
                                                    const trackerType = location.state?.trackerType || params.get('type') || params.get('trackerType');
                                                    const fromTab = location.state?.from || params.get('from') || (trackerType === 'youtube' ? 'youtube_tracker' : 'social_tracker');
                                                    navigate(ROUTES.accountOverview.path, { state: { restoreTab: fromTab } });
                                                }
                                                else if (location.pathname.startsWith(`${ROUTES.createReport.path}/`)) {
                                                    navigate(ROUTES.createReport.path);
                                                }
                                                else
                                                    navigate(-1);
                                            };
                                            return (_jsx(IconButton, { size: "small", "aria-label": "Back", onClick: handleBack, disabled: isBackDisabled, sx: {
                                                    color: '#71717A',
                                                    cursor: !isBackDisabled ? 'pointer' : 'default',
                                                    '&.Mui-disabled': {
                                                        color: '#71717A',
                                                        opacity: 0.5,
                                                    },
                                                    // Remove hover effect if not an inner page
                                                    '&:hover': {
                                                        bgcolor: !isBackDisabled ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                                    }
                                                }, children: _jsx(SvgIcon, { viewBox: "0 0 24 24", sx: {
                                                        width: 24,
                                                        height: 24,
                                                    }, children: _jsx("path", { d: "M20 12H4M4 12L10 18M4 12L10 6", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }) }));
                                        })(), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 600, color: '#000', whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: pageTitle })] }), _jsx(Box, { sx: { flex: 1 } }), !isClient && (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, mr: 2 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 500, color: '#71717A' }, children: "Presentation Mode" }), _jsx(IOSSwitch, { checked: isPresentationMode, onChange: handlePresentationToggle })] })), !isClient && ((location.pathname === ROUTES.accountOverview.path && activeTab === 'account_overview') ||
                                    location.pathname === ROUTES.divergenceReport.path ||
                                    location.pathname === ROUTES.emailMarketingReport.path ||
                                    location.pathname === ROUTES.marketingCalender.path ||
                                    location.pathname === ROUTES.accountPerformanceReport.path ||
                                    location.pathname === ROUTES.syncLog.path) && (_jsx(Box, { sx: { display: "flex", alignItems: "center" }, children: _jsx(AccountOverviewTabs, {}) }))] })] }) }), _jsx(PresentationModals, { openConfirmModal: openConfirmModal, setOpenConfirmModal: setOpenConfirmModal, openSelectStoreModal: openSelectStoreModal, setOpenSelectStoreModal: setOpenSelectStoreModal, isClientListingPage: isClientListingPage })] }));
}
const AccountOverviewTabs = () => {
    const { isArchiveMode, setIsArchiveMode } = useAccountOverview();
    return (_jsxs(Box, { sx: {
            display: 'flex',
            background: '#F2F4F7',
            borderRadius: '8px',
            padding: '4px',
            border: '1px solid #E9EAEB',
            height: '38px',
            alignItems: 'center'
        }, children: [_jsxs(Box, { onClick: () => setIsArchiveMode(false), sx: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    height: '100%',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: !isArchiveMode ? '#fff' : 'transparent',
                    boxShadow: !isArchiveMode ? '0px 1px 2px rgba(16,24,40,0.05)' : 'none',
                    transition: 'all 0.2s ease'
                }, children: [_jsx(Box, { sx: {
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: !isArchiveMode ? '#12B76A' : '#98A2B3',
                            transition: 'all 0.2s ease'
                        } }), _jsx(Typography, { sx: {
                            fontWeight: 500,
                            fontSize: '12px',
                            color: !isArchiveMode ? '#101828' : '#667085',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease'
                        }, children: "Active" })] }), _jsxs(Box, { onClick: () => setIsArchiveMode(true), sx: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    height: '100%',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: isArchiveMode ? '#fff' : 'transparent',
                    boxShadow: isArchiveMode ? '0px 1px 2px rgba(16,24,40,0.05)' : 'none',
                    transition: 'all 0.2s ease'
                }, children: [_jsx(Box, { sx: {
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: isArchiveMode ? '#667085' : '#98A2B3',
                            transition: 'all 0.2s ease'
                        } }), _jsx(Typography, { sx: {
                            fontWeight: 500,
                            fontSize: '12px',
                            color: isArchiveMode ? '#101828' : '#667085',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease'
                        }, children: "Archive" })] })] }));
};
export default memo(DashboardHeader);
